/**
 * Spec 0044 — per-product internal API key management (saas-auth SASATH-0052).
 *
 * Three SUPER_ADMIN-gated methods on saas-auth's existing `/api/saas-admin/**`
 * surface: list / issue / revoke. A product may have multiple ACTIVE keys
 * (zero-downtime rotation); the raw key is returned by `issue` exactly once.
 *
 * ENVELOPE: the shipped SASATH-0052 U3 (saas-auth `main`, commit dd784d2,
 * PR #165) wraps every response in the standard `ApiResponse<T>` envelope
 * (verified against the shipped `SaasAdminController` + DTOs), so this module
 * uses the strict `request()` + `unwrap()` pattern of the other `api/admin/*`
 * modules — NOT the tolerant parsing 0044 needed while the shape was
 * unconfirmed. The confirmed payloads: issue → `{ rawKey, key }`
 * (`InternalKeyCreatedDto`); list → a bare array of `InternalKeyDto`; revoke →
 * a single `InternalKeyDto`. See `types.ts` CONTRACT STATUS.
 */
import { request, unwrap } from '../client';
import type {
  ApiResponse,
  InternalKeyMetadata,
  IssueInternalKeyRequest,
  IssueInternalKeyResponse,
} from '../types';

const base = (productId: number) => `/api/saas-admin/products/${productId}/internal-keys`;

export const internalKeysApi = {
  async list(productId: number): Promise<InternalKeyMetadata[]> {
    const res = await request<ApiResponse<InternalKeyMetadata[]>>('GET', base(productId));
    return unwrap(res);
  },

  async issue(
    productId: number,
    req: IssueInternalKeyRequest = {},
  ): Promise<IssueInternalKeyResponse> {
    const res = await request<ApiResponse<IssueInternalKeyResponse>>('POST', base(productId), {
      json: req,
    });
    return unwrap(res);
  },

  async revoke(productId: number, keyId: number): Promise<InternalKeyMetadata> {
    const res = await request<ApiResponse<InternalKeyMetadata>>(
      'POST',
      `${base(productId)}/${keyId}/revoke`,
    );
    return unwrap(res);
  },
};
